import{ useEffect, useState } from 'react';
import { isEmpty } from 'lodash';

import type { RcFile } from 'antd/lib/upload';

import { GeneralDocumentsUploadType, UploadService, useUploadDocument } from 'modules/entities/modules/documents';

import { GeneralUploadTypeInput } from '../GeneralUploadTypeInput';
import { GeneralUploadFileInput } from '../GeneralUploadFileInput';
import { GeneralUploadButtons } from '../GeneralUploadButtons';
import { useNotifications } from 'modules/ui';
import { useIntl } from 'react-intl';
import useUser from 'modules/auth/hooks/useUser';
import config from 'config';

const defaultFiles = [];

const GENERAL_UPLOAD_ID = 'general-upload';

interface GeneralUploadProps {
    hideModal?: () => void;
    onUploadSuccess?: () => void;
}

export const GeneralUpload = ({ hideModal, onUploadSuccess }: GeneralUploadProps) => {
    const notification = useNotifications();
    const intl = useIntl();
    const user = useUser();

    const { api, uploadDocument, resetUpload } = useUploadDocument(GENERAL_UPLOAD_ID);

    const [files, setFiles] = useState<RcFile[]>(defaultFiles);
    const [type, setType] = useState<GeneralDocumentsUploadType>(null);
    const [quickUploading, setQuickUploading] = useState(false);

    useEffect(() => {
        if (api.lastSuccessAt) {
            notification.success({
                message: intl.formatMessage({
                    id: 'documents.generalUpload.success.title',
                }),
                description: intl.formatMessage({
                    id: 'documents.generalUpload.success.description',
                }),
            });
            if (onUploadSuccess) {
                onUploadSuccess();
            }
        }
    }, [api.lastSuccessAt, notification, intl]);

    useEffect(() => {
        if (api.error) {
            notification.error({
                message: intl.formatMessage({
                    id: 'documents.generalUpload.error.title',
                }),
                description: intl.formatMessage({
                    id: 'documents.generalUpload.error.description',
                }),
            });
        }
    }, [api.error, notification, intl]);

    useEffect(() => {
        if (api.lastSuccessAt && (type !== null || files !== defaultFiles)) {
            setType(null);
            setFiles(defaultFiles);
        }
    }, [api.lastSuccessAt, files, type]);

    useEffect(() => {
        if (!quickUploading && type === null && files !== defaultFiles && !files.length) {
            setType(null);
        }
    }, [files, quickUploading, type]);

    useEffect(() => {
        if (api.lastSuccessAt || api.error) {
            resetUpload();
        }
    }, [api.lastSuccessAt, api.error, resetUpload]);

    return (
        <>
            <GeneralUploadTypeInput value={type} onChange={setType} disabled={api.inProgress || quickUploading} />
            <GeneralUploadFileInput value={files} onChange={setFiles} disabled={api.inProgress || quickUploading} />
            <GeneralUploadButtons
                loading={api.inProgress || quickUploading}
                disabled={isEmpty(files) || !type || api.inProgress || quickUploading}
                onSubmit={async () => {
                    if (
                        type === GeneralDocumentsUploadType.QUICK_ANALYSIS
                        || type === GeneralDocumentsUploadType.QUICK_ANALYSIS_ASSESSMENT_REPORT
                    ) {
                        if (!config.quickAnalysisApiKey) {
                            notification.error({
                                message: intl.formatMessage({
                                    id: 'documents.quickAnalysis.error.title',
                                }),
                                description: intl.formatMessage({
                                    id: 'documents.quickAnalysis.error.missingApiKey',
                                }),
                            });
                            return;
                        }

                        const formData = new FormData();
                        files.forEach(file => {
                            formData.append('files', file);
                        });
                        formData.append('userInfo', JSON.stringify({
                            email: user?.email || '',
                            name: (user as any)?.name || user?.username || '',
                            id: (user as any)?.id || user?.customUserId || user?.username || '',
                        }));

                        const uploadEndpoint = type === GeneralDocumentsUploadType.QUICK_ANALYSIS_ASSESSMENT_REPORT
                            ? config.api.quickAnalysisAssessmentReportUpload
                            : config.api.quickAnalysisUpload;

                        setQuickUploading(true);
                        try {
                            const response = await fetch(`${config.api.base}${uploadEndpoint}`, {
                                method: 'POST',
                                headers: {
                                    'x-api-key': config.quickAnalysisApiKey,
                                },
                                body: formData,
                            });

                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}`);
                            }

                            // For assessment reports, each uploaded file is tracked as a
                            // CustomerDocument case (landingPageDocId) — surface it so staff
                            // have a reference even though the Landing pages tab will pick
                            // the same document up automatically once Rossum matches it.
                            const uploadResult = await response.json().catch(() => null) as {
                                files?: { landingPageDocId?: string }[];
                            } | null;
                            const caseIds = (uploadResult?.files ?? [])
                                .map(file => file.landingPageDocId)
                                .filter((id): id is string => !!id);

                            notification.success({
                                message: intl.formatMessage({
                                    id: 'documents.quickAnalysis.success.title',
                                }),
                                description: caseIds.length
                                    ? intl.formatMessage(
                                        { id: 'documents.quickAnalysis.success.descriptionWithCase', defaultMessage: 'Tracked as case {caseIds}.' },
                                        { caseIds: caseIds.join(', ') },
                                    )
                                    : intl.formatMessage({
                                        id: 'documents.quickAnalysis.success.description',
                                    }),
                            });
                            setType(null);
                            setFiles(defaultFiles);
                            if (onUploadSuccess) {
                                onUploadSuccess();
                            }
                        } catch {
                            notification.error({
                                message: intl.formatMessage({
                                    id: 'documents.quickAnalysis.error.title',
                                }),
                                description: intl.formatMessage({
                                    id: 'documents.quickAnalysis.error.description',
                                }),
                            });
                        } finally {
                            setQuickUploading(false);
                        }
                        return;
                    }

                    uploadDocument({
                        files: files.map(file => ({
                            fileUrl: URL.createObjectURL(file),
                            name: file.name,
                        })),
                        service: type === GeneralDocumentsUploadType.ONBOARDING
                            ? UploadService.ONBOARDING
                            : UploadService.GENERAL_DOCUMENTS,
                        type,
                    });
                }}
                hideModal={hideModal}
            />
        </>
    );
};
